import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SFIconButton from "../buttons/iconButton";
import { SFCloseIcon } from "../icons";
import { SFStack, SFUseStyled } from "@sf-core";
import { useTranslation } from "react-i18next";

const { styled } = SFUseStyled();

const DialogStyled = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== "lightBackgroundDialog",
})(({ theme, lightBackgroundDialog }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: theme.palette.grey550.main,
    color: theme.palette.grey200.main,
    borderRadius: "6px",
    position: "relative",
    ...(lightBackgroundDialog && {
      backgroundColor: theme.palette.grey450.main,
    }),
  },
}));

const BackgroundThumbnail = styled("div", {
  shouldForwardProp: (prop) => prop !== "backgroundThumbnail",
})(({ theme, backgroundThumbnail }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  background: `url(${backgroundThumbnail})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  width: "100%",
  height: "40%",
}));

const BackgroundGradient = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "lightBackgroundDialog" && prop !== "titleThumbnail",
})(({ theme, lightBackgroundDialog, titleThumbnail }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  background: "linear-gradient(rgba(45, 45, 60, 0.3), #2E2E3C)",
  width: "100%",
  height: "40%",
  ...(lightBackgroundDialog && {
    background: "linear-gradient(rgba(45, 45, 60, 0.3), #3A3A4A)",
  }),
  ...(titleThumbnail && {
    height: "100%",
    zIndex: 0,
  }),
}));

const DialogTitleThumbnail = styled("div", {
  shouldForwardProp: (prop) => prop !== "titleThumbnail",
})(({ theme, titleThumbnail }) => ({
  position: "relative",
  width: "100%",
  height: "100%px",
  borderRadius: "6px 6px 0px 0px",
  padding: 0,
  ...(titleThumbnail && {
    background: `url(${titleThumbnail})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingLeft: theme.spacing(4),
  }),
}));

const DialogTitleStyled = styled(DialogTitle, {
  shouldForwardProp: (prop) =>
    prop !== "backgroundVideo" && prop !== "titleThumbnail",
})(({ theme, backgroundVideo, titleThumbnail }) => ({
  margin: 0,
  padding: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(3.5),
  maxHeight: "650px",
  overflow: "auto",
  "&::-webkit-scrollbar": {
    // Hide scrollbar
    display: "none",
  },
  ...(titleThumbnail && {
    position: "relative",
    zIndex: 1,
    padding: 0,
    paddingRight: theme.spacing(4),
  }),
  ...(backgroundVideo && {
    zIndex: 1,
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "180px",
    borderRadius: "6px 6px 0px 0px",
    background:
      "linear-gradient(90deg, rgba(0, 0, 0, 0.60) 26%, rgba(0, 0, 0, 0.3) 64.31%)",
  }),
}));

const StyledDialogContent = styled(DialogContent, {
  shouldForwardProp: (prop) =>
    prop !== "backgroundVideo" && prop !== "contentZeroPadding",
})(({ theme, contentZeroPadding, backgroundVideo }) => ({
  padding: theme.spacing(1, 4, 3, 4),
  overflow: "auto",
  "&::-webkit-scrollbar": {
    // Hide scrollbar
    display: "none",
  },
  ...(contentZeroPadding && {
    padding: 0,
  }),
  ...(backgroundVideo && {
    minHeight: "100px",
  }),
}));

const StyledIconButton = styled(SFIconButton)(({ theme }) => ({
  position: "absolute",
  right: 12,
  top: 20,
  color: theme.palette.white,
}));

const SFVideoBackgroundStyled = styled("video")({
  width: "100%",
  height: "180px",
  objectFit: "cover",
  borderRadius: "6px 6px 0px 0px",
});

const StyledLine = styled("div", {
  shouldForwardProp: (prop) => prop !== "divideLine",
})(({ theme, divideLine }) => ({
  borderBottom: "1px solid",
  borderColor: theme.palette.grey400.main,
  paddingTop: theme.spacing(2),
  opacity: 0.5,
  ...(divideLine && {
    padding: 0,
  }),
}));

const SFDialogTitle = (props) => {
  const {
    children,
    onClose,
    backgroundVideo,
    titleThumbnail,
    titleBottomLine,
  } = props;
  const { t } = useTranslation();
  return (
    <>
      {backgroundVideo && (
        <SFVideoBackgroundStyled autoPlay loop muted src={backgroundVideo} />
      )}
      <DialogTitleStyled
        backgroundVideo={backgroundVideo}
        titleThumbnail={titleThumbnail}
      >
        <SFStack direction="row" spacing={6}>
          <SFStack>{children}</SFStack>
          {onClose && (
            <SFStack>
              <StyledIconButton variant="transparent" onClick={onClose} ariaLabel={t("components.modal.closeButton")}>
                <SFCloseIcon />
              </StyledIconButton>
            </SFStack>
          )}
        </SFStack>
        {titleBottomLine && <StyledLine />}
      </DialogTitleStyled>
    </>
  );
};

const StyledDialogActions = styled(DialogActions, {
  shouldForwardProp: (prop) => prop !== "actionsBoxShadow",
})(({ theme, actionsBoxShadow }) => ({
  padding: theme.spacing(2.5),
  paddingLeft: theme.spacing(4),
  justifyContent: "flex-start",
  ...(actionsBoxShadow && {
    boxShadow: "0px -5px 16px 0px rgba(0, 0, 0, 0.15)",
  }),
}));

const SFDialog = (props) => {
  const scrollproperty = "paper";
  return (
    <DialogStyled
      TransitionComponent={props.TransitionComponent}
      aria-labelledby="dialog-title"
      open={props.open}
      maxWidth={props.size}
      fullWidth={props.fullWidth}
      width={props.width}
      onClose={props.onClose}
      scroll={scrollproperty}
      lightBackgroundDialog={props.lightBackgroundDialog}
    >
      {props.backgroundThumbnail && (
        <>
          <BackgroundThumbnail
            backgroundThumbnail={props.backgroundThumbnail}
          />
          <BackgroundGradient
            lightBackgroundDialog={props.lightBackgroundDialog}
          />
        </>
      )}
      <DialogTitleThumbnail titleThumbnail={props.titleThumbnail}>
        {props.titleThumbnail && (
          <BackgroundGradient
            titleThumbnail={props.titleThumbnail}
            lightBackgroundDialog={props.lightBackgroundDialog}
          />
        )}
        <SFDialogTitle
          id="customized-dialog-title"
          {...(props.onClose && { onClose: props.onClose })}
          backgroundVideo={props.backgroundVideo}
          titleThumbnail={props.titleThumbnail}
          titleBottomLine={props.titleBottomLine}
        >
          {props.header}
        </SFDialogTitle>
      </DialogTitleThumbnail>
      <StyledDialogContent
        contentZeroPadding={props.contentZeroPadding}
        backgroundVideo={props.backgroundVideo}
      >
        {props.children}
      </StyledDialogContent>
      {props.divideLine && <StyledLine divideLine={props.divideLine} />}
      {props.actionBtns && (
        <>
          <StyledDialogActions actionsBoxShadow={props.actionsBoxShadow}>
            {props.actionBtns}
          </StyledDialogActions>
        </>
      )}
    </DialogStyled>
  );
};

export default SFDialog;
